"use client"

/**
 * Checks if the browser supports the Notification API.
 * @returns {boolean} Returns true if the Notification API is available, otherwise false.
 */
function isAvailable() {
  return "Notification" in window
}

/**
 * Requests permission for displaying notifications.
 * @returns A promise that resolves to the permission status.
 */
function requestPermission() {
  return Notification.requestPermission()
}

/**
 * Checks if the browser has granted permission for notifications.
 * @returns {boolean} Returns true if permission is granted, otherwise false.
 */
function hasPermission() {
  return Notification.permission === "granted"
}

/**
 * Sends a notification with the specified title and options.
 * @param title - The title of the notification.
 * @param options - The options for the notification.
 * @returns A promise that resolves to an object with the result of the notification.
 */
async function sendNotification(
  title: string,
  options?: NotificationOptions
): Promise<{
  result: "sent" | "denied" | "not-available" | "unknown"
}> {
  if (!isAvailable()) {
    return { result: "not-available" }
  }

  if (!hasPermission()) {
    const res = await requestPermission()

    if (res !== "granted") {
      return { result: "denied" }
    }
  }

  new Notification(title, {
    icon: "/assets/ringio_icon.png",
    ...options,
  })
  return { result: "sent" }
}

/**
 * Custom hook for managing action center notifications.
 *
 * @returns An object containing functions and properties related to action center notifications.
 */
export function useActionCenterNotifications() {
  // NOTE: Maybe in the future, migrate this to use a Service Worker, so we can use persistent notifications with actions:
  // https://learn.microsoft.com/en-us/microsoft-edge/progressive-web-apps-chromium/how-to/notifications-badges#add-actions-to-notifications
  return {
    isAvailable,
    requestPermission,
    hasPermission,
    sendNotification,
  }
}
